"use client";

import dynamic from "next/dynamic";
import type { ComponentType } from "react";
import type { Params, ScreenKey } from "@/core/screen-meta";
import { ErrorState } from "@/components/misc";
import { Spinner } from "@/components/ui/spinner";

export type ScreenComponent = ComponentType<{ params: Params }>;

function Carregando() {
  return (
    <div className="flex flex-1 items-center justify-center text-muted">
      <Spinner className="size-4" />
    </div>
  );
}

/** Cada tela vira um pedaço separado do bundle: só baixa quando abre pela primeira vez. */
const tela = <M extends object>(carregar: () => Promise<M>, escolher: (m: M) => ScreenComponent): ScreenComponent =>
  dynamic(() => carregar().then((m) => ({ default: escolher(m) })), { loading: Carregando, ssr: false });

function ErroScreen({ params }: { params: Params }) {
  const c = params.codigo;
  return <ErrorState code={c === "403" || c === "404" || c === "offline" ? c : "500"} />;
}

const InicioScreen = tela(() => import("@/features/inicio/inicio-screen"), (m) => m.InicioScreen);
const CteListScreen = tela(() => import("@/features/cte/screens"), (m) => m.CteListScreen);
const MdfeScreen = tela(() => import("@/features/mdfe/screens/mdfe-screen"), (m) => m.MdfeScreen);
const CiotScreen = tela(() => import("@/features/ciot/ciot-screen"), (m) => m.CiotScreen);

/** No desktop, a aba "Documentos" do mobile cai na lista do tipo pedido. */
function DocumentosScreen({ params }: { params: Params }) {
  if (params.tipo === "mdfe") return <MdfeScreen params={params} />;
  if (params.tipo === "ciot") return <CiotScreen params={params} />;
  return <CteListScreen params={params} />;
}

/**
 * Registro das telas do ERP (desktop). O mobile reaproveita este mapa para as telas que não têm
 * versão própria (ver mobile/MobileApp.tsx). Chave = segmento da URL (/app/<chave>).
 */
export const SCREENS: Record<ScreenKey, ScreenComponent> = {
  inicio: InicioScreen,
  "nova-viagem": tela(() => import("@/features/viagens/nova-viagem/nova-viagem-screen"), (m) => m.NovaViagemScreen),
  viagens: tela(() => import("@/features/viagens/screens/viagens-screen"), (m) => m.ViagensScreen),
  viagem: tela(() => import("@/features/viagens/screens/viagem-screen"), (m) => m.ViagemScreen),
  ctes: CteListScreen,
  cte: tela(() => import("@/features/cte/screens"), (m) => m.CteScreen),
  "cte-complemento": tela(() => import("@/features/cte/screens"), (m) => m.CteComplementoScreen),
  mdfes: MdfeScreen,
  mdfe: tela(() => import("@/features/mdfe/screens/mdfe-screen"), (m) => m.MdfeDetalheScreen),
  ciots: CiotScreen,
  ciot: tela(() => import("@/features/ciot/ciot-screen"), (m) => m.CiotDetalheScreen),
  averbacoes: tela(() => import("@/features/averbacao/averbacoes-screen"), (m) => m.AverbacoesScreen),
  eventos: tela(() => import("@/features/eventos/eventos-screen"), (m) => m.EventosScreen),
  "pagamentos-tac": tela(() => import("@/features/financeiro/screens"), (m) => m.PagamentosTacScreen),
  "contas-receber": tela(() => import("@/features/financeiro/titulos"), (m) => m.ContasReceberScreen),
  "contas-pagar": tela(() => import("@/features/financeiro/titulos"), (m) => m.ContasPagarScreen),
  relatorios: tela(() => import("@/features/financeiro/titulos"), (m) => m.RelatoriosScreen),
  quitacoes: tela(() => import("@/features/financeiro/screens"), (m) => m.QuitacoesScreen),
  exportacoes: tela(() => import("@/features/financeiro/screens"), (m) => m.ExportacoesScreen),
  tomadores: tela(() => import("@/features/cadastros/tomadores"), (m) => m.TomadoresScreen),
  tomador: tela(() => import("@/features/cadastros/tomadores"), (m) => m.TomadorFormScreen),
  veiculos: tela(() => import("@/features/cadastros/veiculos"), (m) => m.VeiculosScreen),
  veiculo: tela(() => import("@/features/cadastros/veiculos"), (m) => m.VeiculoFormScreen),
  motoristas: tela(() => import("@/features/cadastros/motoristas"), (m) => m.MotoristasScreen),
  motorista: tela(() => import("@/features/cadastros/motoristas"), (m) => m.MotoristaFormScreen),
  "tabelas-frete": tela(() => import("@/features/frete/tabelas-screen"), (m) => m.TabelasFreteScreen),
  "tabela-frete": tela(() => import("@/features/frete/tabela-editor"), (m) => m.TabelaFreteEditor),
  "regioes-frete": tela(() => import("@/features/frete/regioes-screen"), (m) => m.RegioesFreteScreen),
  "componentes-frete": tela(() => import("@/features/frete/componentes-screen"), (m) => m.ComponentesFreteScreen),
  apolices: tela(() => import("@/features/cadastros/tabelas-apolices"), (m) => m.ApolicesScreen),
  apolice: tela(() => import("@/features/cadastros/tabelas-apolices"), (m) => m.ApoliceFormScreen),
  empresa: tela(() => import("@/features/sistema/empresa"), (m) => m.EmpresaScreen),
  certificado: tela(() => import("@/features/sistema/empresa"), (m) => m.CertificadoScreen),
  ambiente: tela(() => import("@/features/sistema/empresa"), (m) => m.AmbienteScreen),
  series: tela(() => import("@/features/sistema/empresa"), (m) => m.SeriesScreen),
  "regras-icms": tela(() => import("@/features/sistema/regras-icms"), (m) => m.RegrasIcmsScreen),
  integracoes: tela(() => import("@/features/sistema/empresa"), (m) => m.IntegracoesScreen),
  usuarios: tela(() => import("@/features/sistema/usuarios-prefs"), (m) => m.UsuariosScreen),
  assinatura: tela(() => import("@/features/assinatura/assinatura-screen"), (m) => m.AssinaturaScreen),
  preferencias: tela(() => import("@/features/sistema/usuarios-prefs"), (m) => m.PreferenciasScreen),
  perfil: tela(() => import("@/features/sistema/usuarios-prefs"), (m) => m.PerfilScreen),
  notificacoes: tela(() => import("@/features/sistema/usuarios-prefs"), (m) => m.NotificacoesScreen),
  "sefaz-status": tela(() => import("@/features/sistema/sefaz-status-screen"), (m) => m.SefazStatusScreen),
  documentos: DocumentosScreen,
  // Só existem no app mobile; no desktop caem no Início
  menu: InicioScreen,
  busca: InicioScreen,
  erro: ErroScreen,
};
